/**
 * Source identities are stable business keys, not display labels. An adapter
 * for a future marketplace must receive its own key before it writes an order,
 * payout, or catalogue aggregate.
 */
export const YANDEX_MARKET_PRIMARY_SOURCE = 'ymarket:primary' as const

export type SalesSourceId = string

export interface SalesSourceDefinition {
  id: SalesSourceId
  provider: 'ymarket' | 'other'
  label: string
  /** Contract-specific delay between the accrual period and its payout period. */
  payoutDelayWeeks?: 1 | 2 | 4
}

export const DEFAULT_SALES_SOURCES: readonly SalesSourceDefinition[] = [
  { id: YANDEX_MARKET_PRIMARY_SOURCE, provider: 'ymarket', label: 'Яндекс Маркет', payoutDelayWeeks: 2 },
]

export function salesSourceDefinition(sourceId: SalesSourceId): SalesSourceDefinition {
  const normalized = normalizeSalesSourceId(sourceId)
  return DEFAULT_SALES_SOURCES.find((source) => source.id === normalized)
    || { id: normalized, provider: 'other', label: normalized }
}

export function normalizeSalesSourceId(value: unknown): SalesSourceId {
  if (typeof value !== 'string') return YANDEX_MARKET_PRIMARY_SOURCE
  const normalized = value.trim().toLowerCase()
  return /^[a-z][a-z0-9_-]{1,31}:[a-z][a-z0-9_-]{1,63}$/.test(normalized)
    ? normalized
    : YANDEX_MARKET_PRIMARY_SOURCE
}

function isSalesSourceId(value: unknown): value is SalesSourceId {
  return typeof value === 'string' && /^[a-z][a-z0-9_-]{1,31}:[a-z][a-z0-9_-]{1,63}$/.test(value.trim().toLowerCase())
}

/** Reject invalid adapter input; fallback-to-primary is legacy-read behavior only. */
export function requireSalesSourceId(value: unknown): SalesSourceId {
  if (!isSalesSourceId(value)) throw new Error('Invalid sales source identifier')
  return value.trim().toLowerCase()
}

/** Prevents external-order-ID collisions across future marketplaces. */
export function salesSourceOrderKey(sourceId: SalesSourceId, orderId: number): string {
  if (!Number.isSafeInteger(orderId) || orderId < 1) throw new Error('Invalid source order identifier')
  return `${requireSalesSourceId(sourceId)}:${orderId}`
}

/** Stable route identity for a provider customer; display names are never keys. */
export function salesSourceCustomerKey(sourceId: SalesSourceId, customerId: string): string {
  const normalizedCustomerId = customerId.trim()
  if (!normalizedCustomerId) throw new Error('Invalid source customer identifier')
  return `${requireSalesSourceId(sourceId)}::${normalizedCustomerId}`
}

/** Re-keys a legacy snapshot without relying on its old Redis hash field. */
export function sourceSnapshotKey(snapshot: { sourceId?: SalesSourceId; orderId: number }): string {
  return salesSourceOrderKey(snapshot.sourceId || YANDEX_MARKET_PRIMARY_SOURCE, snapshot.orderId)
}

/**
 * Converts legacy hash values to source-qualified entries without trusting the
 * legacy hash field. A corrupt legacy row is reported as a count, never allowed
 * to block migration of healthy history.
 */
export function sourceSnapshotMigrationEntries<T extends { sourceId?: SalesSourceId; orderId: number }>(
  snapshots: readonly T[],
  existingKeys: ReadonlySet<string> = new Set(),
): { entries: Record<string, T>; skipped: number } {
  const entries: Record<string, T> = {}
  let skipped = 0
  for (const snapshot of snapshots) {
    try {
      const key = sourceSnapshotKey(snapshot)
      if (!existingKeys.has(key)) entries[key] = snapshot
    } catch {
      skipped += 1
    }
  }
  return { entries, skipped }
}
